import React, { useContext, useState, useEffect } from 'react';
import { ApRadio, ApCheckbox } from '@alixpartners/ui-components';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { getPeriodString } from 'utils/date';
import { getPeriodsInfo } from 'utils/apiRequests';
import { RowsCount } from './RowsCount';
import {
	RadioWrapper,
	RadioGroup,
	InnerWrapper,
	InnerGroup,
	HelperText,
} from './PeriodConfigStep.styles';

export const PERIOD_TYPES = {
	full: 0,
	custom: 1,
	years: 2,
};

export const PeriodConfigStep = ({ onParamsChange }) => {
	const [periodType, setPeriodType] = useState(0);
	const [spendType, setSpendType] = useState(0);
	const [dateRangeIds, setDateRangeIds] = useState([]);
	const [periodsInfo, setPeriodsInfo] = useState(null);
	const { options } = useContext(SettingsOptionsContext);
	const { dateRangeId: currentDateRangeId } = useContext(QueryStringContext);
	const customPeriods = options.dateRanges.filter(item => item.isCustom);
	const calendarYears = options.dateRanges.filter(item => !item.isCustom);
	const rowsCountKey =
		spendType === 0 ? 'directRowsCount' : 'indirectRowsCount';

	useEffect(() => {
		getPeriodsInfo()
			.then(data => {
				setPeriodsInfo(data);
			})
			.catch(() => {});
	}, []);

	useEffect(
		() => {
			onParamsChange(getParams());
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[periodType, spendType, dateRangeIds]
	);

	const getParams = () => {
		if (periodType === PERIOD_TYPES.years && dateRangeIds.length === 0) {
			return null;
		}

		let params = {};

		switch (periodType) {
			case PERIOD_TYPES.years: {
				params = {
					years: dateRangeIds.map(rangeId => {
						const dateItem = calendarYears.find(item => item.id === rangeId);

						return dateItem.year;
					}),
				};
				break;
			}
			case PERIOD_TYPES.custom: {
				const dateItem = customPeriods.find(
					item => item.id === dateRangeIds[0]
				);
				const { dateFrom, dateTo } = dateItem;

				params = { dateFrom, dateTo };
				break;
			}
			default: {
				break;
			}
		}
		params.spendType = spendType;

		return params;
	};

	const changePeriodType = e => {
		const type = Number(e.target.value);
		setPeriodType(type);

		if (type === PERIOD_TYPES.custom) {
			setDateRangeIds([customPeriods[0].id]);
		} else {
			setDateRangeIds([]);
		}
	};

	const changeSpendType = e => {
		const type = Number(e.target.value);
		setSpendType(type);
	};

	const handleCustomPeriodClick = e => {
		setDateRangeIds([Number(e.target.value)]);
	};

	const handleCalendarYearClick = e => {
		const value = Number(e.target.value);

		if (dateRangeIds.includes(value)) {
			return setDateRangeIds(dateRangeIds.filter(item => item !== value));
		}

		setDateRangeIds([...dateRangeIds, value]);
	};

	const getCalendarYearsTotal = () => {
		if (!periodsInfo) {
			return;
		}

		return dateRangeIds.reduce((acc, dateRangeId) => {
			const dateRange =
				calendarYears.find(item => item.id === dateRangeId) || {};
			const periodInfo =
				periodsInfo.calendarYears &&
				periodsInfo.calendarYears.find(item => item.year === dateRange.year);

			return acc + (periodInfo ? periodInfo[rowsCountKey] : 0);
		}, 0);
	};

	return (
		<div>
			<RadioWrapper>
				<ApRadio
					name="spend-type"
					value={0}
					checked={spendType === 0}
					onChange={changeSpendType}
				>
					Direct spend
				</ApRadio>
			</RadioWrapper>
			<RadioWrapper>
				<ApRadio
					name="spend-type"
					value={1}
					checked={spendType === 1}
					onChange={changeSpendType}
				>
					Indirect spend
				</ApRadio>
			</RadioWrapper>
			You can download the full spend cube or choose a smaller data set based on
			a selected time period.
			<RadioGroup>
				<RadioWrapper>
					<ApRadio
						name="download-period"
						value={PERIOD_TYPES.full}
						checked={periodType === PERIOD_TYPES.full}
						onChange={changePeriodType}
					>
						Full Spend Cube{periodsInfo &&
							periodsInfo.fullSpendCube && (
								<RowsCount
									count={periodsInfo.fullSpendCube[rowsCountKey]}
									isTotal
								/>
							)}
					</ApRadio>
				</RadioWrapper>
				{customPeriods.length > 0 && (
					<React.Fragment>
						<RadioWrapper>
							<ApRadio
								name="download-period"
								value={PERIOD_TYPES.custom}
								checked={periodType === PERIOD_TYPES.custom}
								onChange={changePeriodType}
							>
								Time period
							</ApRadio>
						</RadioWrapper>
						{periodType === PERIOD_TYPES.custom && (
							<InnerGroup>
								{customPeriods.map(({ id, name }) => {
									const periodInfoObj =
										periodsInfo &&
										periodsInfo.timePeriods &&
										periodsInfo.timePeriods.find(item => item.name === name);

									return (
										<InnerWrapper key={id}>
											<ApRadio
												name="custom-period"
												value={id}
												checked={dateRangeIds.includes(id)}
												onChange={handleCustomPeriodClick}
											>
												{name}
												{currentDateRangeId === id.toString() && ' (current)'}
												{periodInfoObj && (
													<RowsCount count={periodInfoObj[rowsCountKey]} />
												)}
											</ApRadio>
										</InnerWrapper>
									);
								})}
							</InnerGroup>
						)}
					</React.Fragment>
				)}

				<RadioWrapper>
					<ApRadio
						name="download-period"
						value={PERIOD_TYPES.years}
						checked={periodType === PERIOD_TYPES.years}
						onChange={changePeriodType}
					>
						Calendar year(s){!!getCalendarYearsTotal() && (
							<RowsCount count={getCalendarYearsTotal()} isTotal />
						)}
						{periodType === PERIOD_TYPES.years &&
							dateRangeIds.length === 0 && (
								<HelperText>Please select year(s)</HelperText>
							)}
					</ApRadio>
				</RadioWrapper>
				{periodType === PERIOD_TYPES.years && (
					<InnerGroup>
						{calendarYears
							.filter(calendarYear => calendarYear[rowsCountKey] !== 0)
							.map(({ id, dateFrom, dateTo, year }) => {
								const periodInfoObj =
									periodsInfo &&
									periodsInfo.calendarYears &&
									periodsInfo.calendarYears.find(item => item.year === year);

								return (
									<InnerWrapper key={id}>
										<ApCheckbox
											name="calendar-years"
											value={id}
											checked={dateRangeIds.includes(id)}
											onChange={handleCalendarYearClick}
										>
											{getPeriodString(dateFrom, dateTo)}
											{currentDateRangeId === id.toString() && ' (current)'}
											{periodInfoObj && (
												<RowsCount
													count={periodInfoObj[rowsCountKey]}
													suppressWarning
												/>
											)}
										</ApCheckbox>
									</InnerWrapper>
								);
							})}
					</InnerGroup>
				)}
			</RadioGroup>
		</div>
	);
};
